import React, { useEffect, useState } from 'react';
import { UserPlus, Mail, Shield, RefreshCw } from 'lucide-react';
import API, { formatError } from '../utils/api';
import { toast } from 'sonner';
import { ROLE_OPTIONS } from '../lib/permissions';

const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-700',
  invited: 'bg-amber-50 text-amber-700',
  disabled: 'bg-slate-100 text-slate-600',
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState({ name: '', email: '', role: 'member' });
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    API.get('/admin/users')
      .then((r) => setUsers(r.data || []))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleInvite = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await API.post('/admin/users/invite', invite);
      toast.success(`Invitation sent to ${invite.email}`);
      if (data.invite_url) {
        console.info('Invite URL:', data.invite_url);
      }
      setInvite({ name: '', email: '', role: 'member' });
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const updateUser = async (userId, patch) => {
    try {
      await API.patch(`/admin/users/${userId}`, patch);
      toast.success('User updated');
      load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const resendInvite = async (userId) => {
    try {
      const { data } = await API.post(`/admin/users/${userId}/resend-invite`);
      toast.success('Invitation resent');
      if (data.invite_url) console.info('Invite URL:', data.invite_url);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Outfit' }}>Team access</h1>
        <p className="text-slate-500 text-sm">
          SignatureFlow is invite-only. Add teammates here and assign roles — there is no public sign-up.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 bg-white border border-slate-200 rounded-sm p-5 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={18} className="text-blue-700" />
            <h2 className="font-semibold text-slate-900">Invite user</h2>
          </div>
          <form onSubmit={handleInvite} className="space-y-3">
            <input
              type="text"
              required
              placeholder="Full name"
              value={invite.name}
              onChange={(e) => setInvite((p) => ({ ...p, name: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
            <input
              type="email"
              required
              placeholder="Email address"
              value={invite.email}
              onChange={(e) => setInvite((p) => ({ ...p, email: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            />
            <select
              value={invite.role}
              onChange={(e) => setInvite((p) => ({ ...p, role: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              {ROLE_OPTIONS.find((r) => r.value === invite.role)?.description}
            </p>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-blue-700 hover:bg-blue-800 text-white py-2 rounded-sm text-sm font-medium disabled:opacity-60"
            >
              {submitting ? 'Sending...' : 'Send invitation'}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
            <Shield size={18} className="text-slate-500" />
            <h2 className="font-semibold text-slate-900">Users & roles</h2>
          </div>
          {loading ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading users...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="px-5 py-3 font-medium">User</th>
                    <th className="px-5 py-3 font-medium">Role</th>
                    <th className="px-5 py-3 font-medium">Status</th>
                    <th className="px-5 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100">
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-900">{u.name}</div>
                        <div className="text-slate-500 text-xs flex items-center gap-1">
                          <Mail size={12} /> {u.email}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <select
                          value={u.role || 'member'}
                          onChange={(e) => updateUser(u.id, { role: e.target.value })}
                          className="px-2 py-1 border border-slate-300 rounded-sm text-sm bg-white"
                        >
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[u.status] || STATUS_BADGE.active}`}>
                          {u.status || 'active'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-2">
                          {u.status === 'invited' && (
                            <button
                              type="button"
                              onClick={() => resendInvite(u.id)}
                              className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                            >
                              <RefreshCw size={12} /> Resend
                            </button>
                          )}
                          {u.status !== 'disabled' ? (
                            <button
                              type="button"
                              onClick={() => updateUser(u.id, { status: 'disabled' })}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Disable
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => updateUser(u.id, { status: 'active' })}
                              className="text-xs text-emerald-700 hover:underline"
                            >
                              Enable
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
